import RenderComponent from '@/modules/DynamicRenderPage';
import { COOKIE_AUTH_DETAILS } from 'globalconfig/constants/keys';
import AuthContext from 'globalservices/auth';
import { deleteCookie } from 'globalservices/cookie';
import { loadPageConfig, loadStyle } from 'globalutils/fmUtils';
import PropTypes from 'prop-types';
import React, { lazy, useContext, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import ParentStyled from './style';
import FleetLocateStyled from './fleetLocateStyle';
import {
  fleetLocateLoginPageConfig,
  isSpireonFleetLocateLogin,
  resolveSpireonNewPostLoginPath,
  shouldUseSpireonNewRoutes,
} from './fleetLocateLoginUtils';
import { getRedirectionURL, handleLoginWithToken } from './utils/loginHelpers';

const FleetLocateContent = lazy(() => import('@/pages/web/Login/FleetLocateComponent'));

const Login = ({ history, i18n, fetchLogin, clearRedux, handleLogout,fetchMicrosoftLogin, fetchLoginConfig, login, ...rest }) => {
  const auth = useContext(AuthContext);
  const [redirectedFromFleetops, setRedirectedFromFleetops] = useState(null);
  const [redirectedUrl, setRedirectedUrl] = useState(null);
  const isFleetLocateLogin = isSpireonFleetLocateLogin(history.location.pathname);
  const [webAppConfigurations, setWebAppConfigurations] = useState(null);

  const loginConfig = useMemo(() => {
    if (isFleetLocateLogin) {
      return fleetLocateLoginPageConfig;
    }
    return loadPageConfig().login[0];
  }, [isFleetLocateLogin]);

  const Content = useMemo(() => {
    if (isFleetLocateLogin) {
      return FleetLocateContent;
    }
    const config = loadPageConfig();
    return lazy(() => import(`@/${config.login[0].name}`));
  }, [isFleetLocateLogin]);

  const Styled = useMemo(() => {
    if (isFleetLocateLogin) {
      return FleetLocateStyled;
    }
    return loadStyle(ParentStyled, 'pages/web/Login');
  }, [isFleetLocateLogin]);

  const commonParentProps = { history, Styled, i18n };

  // Fetch login config on component mount
  useEffect(() => {
    fetchLoginConfig({
      apiId: 'webAppConfigurations',
      callback: (response) => {
        setWebAppConfigurations(response);
      }
    });
  }, [fetchLoginConfig]);

  useEffect(() => {
    if (auth?.auth) {
      if ((window.$environment.SERVICE_PROVIDER === 'spireon' ||
        window.$environment.SERVICE_PROVIDER === "nauto") &&
        history.location.state?.search) {
        deleteCookie(COOKIE_AUTH_DETAILS);
        clearRedux();
      } else {
        const redirectionURL = getRedirectionURL(history.location?.search, 'redirect');
        if (redirectionURL && redirectionURL.includes(window.$environment?.FLEETOPS_URL)) {
          setRedirectedFromFleetops(redirectionURL);
        } else {
          history.push(
            shouldUseSpireonNewRoutes(history.location.pathname)
              ? resolveSpireonNewPostLoginPath(history.location.state?.referrer)
              : '/'
          );
        }
      }
    }
  }, [auth, history, clearRedux]);

  useEffect(() => {
    const redirectionURL = getRedirectionURL(history.location?.search, 'redirect');
    if (history.location.state?.search &&
      (window.$environment.SERVICE_PROVIDER === 'spireon' ||
        window.$environment.SERVICE_PROVIDER === 'nauto')) {
      handleLoginWithToken(history, fetchLogin, clearRedux, COOKIE_AUTH_DETAILS);
    } else if (redirectionURL && redirectionURL.includes(window.$environment?.FLEETOPS_URL)) {
      setRedirectedFromFleetops(redirectionURL);
    } else if (redirectionURL) {
      setRedirectedUrl(history.location?.search.replace('?redirect=', ''));
    }
  }, [history, fetchLogin, clearRedux]);

  if (history.location.state?.search && window.$environment.SERVICE_PROVIDER === 'spireon') {
    return null;
  }

  return (
    <RenderComponent
      {...loginConfig}
      parentProps={{ history, i18n, fetchLogin, clearRedux, handleLogout, fetchMicrosoftLogin, fetchLoginConfig, clearRedux, webAppConfigurations }}
      commonParentProps={commonParentProps}
      component={Content}
      redirectedFromFleetops={redirectedFromFleetops}
      redirectedUrl={redirectedUrl}
      {...rest}
    />
  );
};

const mapState = (state) => ({
  apiStatus: state.asyncRequests,
  microsoftLoginApiStatus: state.asyncRequests,
  login: state.login,
});

const mapDispatch = (dispatch) => ({
  fetchLogin: (data) => dispatch.login.fetchData(data),
  fetchMicrosoftLogin: (data) => dispatch.login.fetchMicrosoftLogin(data),
  fetchLoginConfig: (data) => dispatch.login.fetchLoginConfig(data),
  clearRedux: () => dispatch({ type: 'RESET_APP' }),
  handleLogout: (data) => dispatch.login.handleLogout(data),
});

Login.propTypes = {
  login: PropTypes.object.isRequired,
  apiStatus: PropTypes.object.isRequired,
  microsoftLoginApiStatus: PropTypes.object.isRequired,
  fetchLogin: PropTypes.func.isRequired,
  clearRedux: PropTypes.func.isRequired,
  handleLogout: PropTypes.func,
  history: PropTypes.object.isRequired,
  i18n: PropTypes.object.isRequired,
};

export default connect(mapState, mapDispatch)(Login);
